import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Swal from 'sweetalert2';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';
import {
  Grid,
  Card,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import RichTextEditor from "components/RichTextEditor";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { api } from "../../api";

const selectBoxHeight = 45; // Set the height for the select box

function CreateDraftPrescription() {
  const { patientCode, coupleCode } = useParams();
  const navigate = useNavigate();
  
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      weight: "",
      height: "",
      bmi: "",
      bp_systolic: "",
      bp_diastolic: "",
      remarks: "",
      doctor_id: "",
    },
  });

  // State management
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [showSpouseInfo, setShowSpouseInfo] = useState(false);
  const [doctors, setDoctors] = useState([]);

  // Component states for prescription components - REMOVED (will be managed separately)
  // const [observations, setObservations] = useState([
  //   { name: "", value: "", description: "" }
  // ]);
  // const [investigations, setInvestigations] = useState([
  //   { name: "", description: "", instructions: "", priority: "NORMAL" }
  // ]);
  // const [medications, setMedications] = useState([
  //   { name: "", dosage: "", frequency: "", duration: "", instructions: "" }
  // ]);
  // const [scans, setScans] = useState([
  //   { name: "", description: "", instructions: "", priority: "NORMAL" }
  // ]);

  // Load patient data
  useEffect(() => {
    const loadPatientData = async () => {
      if (!patientCode) return;
      
      setLoading(true);
      
      try {
        // Use the same API call as patient investigations page
        const response = await api.contacts.getPatientDetails(patientCode);

        if (response?.data?.contact) {
          const patient = response.data.contact;
          const spouse = response.data?.spouse;

          const patientInfo = {
            id: patient.id,
            name: patient.full_name,
            external_code: patient.external_code,
            couple_code: response.data?.couple_info?.external_code,
            gender: patient.gender,
            phone_number: response.data?.phones?.[0]?.phone_number || "N/A",
            email: patient.email || "N/A",
            occupation: patient.occupation || "N/A",
            nationality_id: patient.irm_countries_id,
            nationality: patient?.nationality,
            marital_status: patient.marital_status,
            married_for: patient.married_for,
            trying_for: patient.trying_for,
            profile_picture_url: patient.profile_picture_base_url && patient.profile_picture_path 
              ? `${patient.profile_picture_base_url}${patient.profile_picture_path}` 
              : null,
            // Additional fields for prescription
            contact_id: patient.id,
            couple_id: response.data?.couple_info?.id || null,
            address: response.data?.addresses?.[0]?.address_line_1 || "N/A",
            date_of_birth: patient.date_of_birth || null,
            age: patient.age || null,
            // Spouse information
            spouse: spouse?.contact ? {
              id: spouse.contact.id,
              name: spouse.contact.full_name,
              external_code: spouse.contact.external_code,
              gender: spouse.contact.gender,
              age: spouse.contact.age,
              occupation: spouse.contact.occupation || "N/A",
              profile_picture_url: spouse.contact.profile_picture_base_url && spouse.contact.profile_picture_path 
                ? `${spouse.contact.profile_picture_base_url}${spouse.contact.profile_picture_path}` 
                : null,
            } : null,
          };
          setPatientData(patientInfo);
          setLoading(false); // Hide loader after successful data load
        } else {
          // Hide loader before showing error alert
          setLoading(false);
          
          // Show error alert for patient not found
          await Swal.fire({
            icon: 'error',
            title: 'Patient Not Found',
            text: 'Patient not found with the provided code',
            showConfirmButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error('Error loading patient data:', error);
        
        // Hide loader before showing error alert
        setLoading(false);
        
        // Show error alert for loading failure
        await Swal.fire({
          icon: 'error',
          title: 'Loading Error',
          text: error.response?.data?.message || 'Failed to load patient data',
          showConfirmButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    };

    loadPatientData();
  }, [patientCode, coupleCode]);

  // Load doctors list
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const response = await api.contacts.getDoctorsList();
        if (response?.data) {
          setDoctors(response.data);
        }
      } catch (error) {
        console.error('Error loading doctors list:', error);
      }
    };

    loadDoctors();
  }, []);

  // Calculate BMI when weight or height changes
  const watchWeight = watch("weight");
  const watchHeight = watch("height");
  const watchSystolic = watch("bp_systolic");
  const watchDiastolic = watch("bp_diastolic");

  useEffect(() => {
    if (watchWeight && watchHeight) {
      const weight = parseFloat(watchWeight);
      const height = parseFloat(watchHeight) / 100; // Convert cm to meters
      if (weight > 0 && height > 0) {
        const bmi = (weight / (height * height)).toFixed(2);
        setValue("bmi", bmi); // Set as number string without units for form field
      } else {
        setValue("bmi", "");
      }
    } else {
      setValue("bmi", "");
    }
  }, [watchWeight, watchHeight, setValue]);

  // Trigger validation for diastolic BP when systolic changes
  useEffect(() => {
    if (watchSystolic && watchDiastolic) {
      // Re-trigger validation for diastolic field
      setValue("bp_diastolic", watchDiastolic, { shouldValidate: true });
    }
  }, [watchSystolic, watchDiastolic, setValue]);

  // Handle form submission
  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      // Validate required patient data
      if (!patientData?.contact_id) {
        throw new Error("Patient information not found. Please go back and select a valid patient.");
      }

      // Validate doctor selection
      if (!data.doctor_id) {
        throw new Error("Please select a doctor before creating the prescription.");
      }

      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toISOString().split('T')[0];

      const prescriptionData = {
        irm_contacts_id: patientData.contact_id,
        irm_couples_id: patientData.couple_id || 1, // Use couple ID if available, otherwise default
        irm_users_id: data.doctor_id ? parseInt(data.doctor_id) : 1, // Use selected doctor contact_id (PatientRegistration format)
        type: "OPD", // Default to OPD
        prescription_date: currentDate,
        weight: data.weight ? `${data.weight} kg` : null,
        height: data.height ? `${data.height} cm` : null,
        bmi: data.bmi ? `${data.bmi} kg/m²` : null,
        bp_systolic: data.bp_systolic ? parseInt(data.bp_systolic) : null,
        bp_diastolic: data.bp_diastolic ? parseInt(data.bp_diastolic) : null,
        remarks: data.remarks || null,
        created_by: data.doctor_id ? parseInt(data.doctor_id) : 1, // Use selected doctor contact_id
      };

      console.log("Creating prescription:", prescriptionData);
      
      // Call the actual API to create prescription
      const response = await api.opd.createPrescription(prescriptionData);
      
      if (response?.data) {
        // Hide loader before showing success alert
        setLoading(false);
        
        // Show success alert with SweetAlert2
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Draft prescription created successfully! Prescription ID: ${response.data.id || 'Generated'}`,
          showConfirmButton: true,
          confirmButtonColor: '#00798E',
          confirmButtonText: 'OK'
        });
        
        // Redirect to the patient's prescription search page instead of reloading
        navigate(`/prescription/patient/${patientCode}/${coupleCode}`);
      } else {
        throw new Error("Failed to create prescription - no response data");
      }
      
    } catch (error) {
      console.error("Error creating prescription:", error);
      
      // Hide loader before showing error alert
      setLoading(false);
      
      // Show error alert with SweetAlert2
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || error.message || "Failed to create prescription",
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  // Component management functions - REMOVED (will be managed separately)
  // All observation, investigation, medication, and scan management functions
  // have been removed as they will be handled in separate component pages

  const toggleSpouseInfo = () => {
    setShowSpouseInfo(!showSpouseInfo);
  };

  const getGenderDisplay = (gender) => {
    switch (gender) {
      case "M": return "Male";
      case "F": return "Female";
      case "O": return "Other";
      case "U": return "Unspecified";
      default: return "N/A";
    }
  };

  const getGenderColor = (gender) => {
    switch (gender) {
      case "M": return "#1976d2";
      case "F": return "#e91e63";
      case "O": return "#ff9800";
      case "U": return "#757575";
      default: return "#9e9e9e";
    }
  };

  const getPatientInitials = (name) => {
    if (!name) return "P";
    const names = name.trim().split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  if (loading && !patientData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={true} />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <Typography variant="h6" color="textSecondary">
                    Loading patient information...
                  </Typography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!patientData && !loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Patient not found or failed to load patient data.
                  </Alert>
                  <button
                    onClick={() => navigate("/patient-list")}
                    style={{
                      padding: '10px 24px',
                      background: '#00798E',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                    }}
                  >
                    Back to Patient List
                  </button>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Create Draft Prescription
                </MDTypography>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/patient-list")}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    padding: '8px 16px',
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                >
                  Back to Patient List
                </Button>
              </MDBox>

              {/* Patient Info Card */}
              {patientData && (
                <MDBox p={3}>
                  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
                      Patient Information
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {/* Patient Avatar and Basic Info */}
                      <Grid item xs={12} md={3}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Avatar
                            src={patientData.profile_picture_url}
                            sx={{
                              width: 120,
                              height: 120,
                              mb: 2,
                              bgcolor: patientData.gender === 'M' ? 'primary.main' : 'secondary.main',
                              fontSize: '2rem',
                              border: "3px solid #e0e0e0"
                            }}
                          >
                            {patientData.name ? patientData.name.charAt(0).toUpperCase() : 'P'}
                          </Avatar>
                          <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                            {patientData.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center">
                            Patient Code: {patientData.external_code}
                          </Typography>
                          {patientData.couple_code && (
                            <Typography variant="body2" color="text.secondary" align="center">
                              Couple Code: {patientData.couple_code}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Patient Details */}
                      <Grid item xs={12} md={9}>
                        <Grid container spacing={2}>
                          {/* Basic Information */}
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Basic Information
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body2">
                                  <strong>Gender:</strong> {getGenderDisplay(patientData.gender)}
                                </Typography>
                                {patientData.age && (
                                  <Typography variant="body2">
                                    <strong>Age:</strong> {patientData.age} years
                                  </Typography>
                                )}
                                {patientData.date_of_birth && (
                                  <Typography variant="body2">
                                    <strong>Date of Birth:</strong> {formatDateDDMMYYYY(patientData.date_of_birth)}
                                  </Typography>
                                )}
                                <Typography variant="body2">
                                  <strong>Marital Status:</strong> {patientData.marital_status || 'N/A'}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Contact Information */}
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Contact Information
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body2">
                                  <strong>Phone:</strong> {patientData.phone_number}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Email:</strong> {patientData.email}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Address:</strong> {patientData.address}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Nationality:</strong> {patientData.nationality || 'N/A'}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Additional Information */}
                          <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Additional Information
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="body2">
                                    <strong>Occupation:</strong> {patientData.occupation}
                                  </Typography>
                                </Grid>
                                {patientData.married_for && (
                                  <Grid item xs={12} sm={4}>
                                    <Typography variant="body2">
                                      <strong>Married For:</strong> {patientData.married_for}
                                    </Typography>
                                  </Grid>
                                )}
                                {patientData.trying_for && (
                                  <Grid item xs={12} sm={4}>
                                    <Typography variant="body2">
                                      <strong>Trying For:</strong> {patientData.trying_for}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Spouse Information Toggle - Show if spouse data is available */}
                    {patientData.spouse && (
                      <Box sx={{ mt: 4 }}>
                        <Box 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="center"
                          sx={{ mb: showSpouseInfo ? 3 : 0 }}
                        >
                          <IconButton
                            onClick={toggleSpouseInfo}
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'white',
                              width: 60,
                              height: 60,
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                            }}
                          >
                            {showSpouseInfo ? (
                              <ExpandLessIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 40 }} />
                            )}
                          </IconButton>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 'bold', 
                              color: 'primary.main',
                              ml: 2
                            }}
                          >
                            {showSpouseInfo ? 'Hide Spouse Information' : 'Show Spouse Information'}
                          </Typography>
                        </Box>
                        
                        {showSpouseInfo && (
                          <>
                            <Divider sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Spouse Information
                              </Typography>
                            </Divider>
                            
                            <Grid container spacing={3}>
                              {/* Spouse Avatar and Basic Info */}
                              <Grid item xs={12} md={3}>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                  <Avatar
                                    src={patientData.spouse.profile_picture_url}
                                    sx={{
                                      width: 100,
                                      height: 100,
                                      mb: 2,
                                      bgcolor: patientData.spouse.gender === 'M' ? 'primary.main' : 'secondary.main',
                                      fontSize: '1.5rem',
                                      border: "2px solid #e0e0e0"
                                    }}
                                  >
                                    {patientData.spouse.name ? patientData.spouse.name.charAt(0).toUpperCase() : 'S'}
                                  </Avatar>
                                  <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                                    {patientData.spouse.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" align="center">
                                    Spouse Code: {patientData.spouse.external_code}
                                  </Typography>
                                </Box>
                              </Grid>

                              {/* Spouse Details */}
                              <Grid item xs={12} md={9}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Spouse Basic Information
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body2">
                                          <strong>Gender:</strong> {getGenderDisplay(patientData.spouse.gender)}
                                        </Typography>
                                        {patientData.spouse.age && (
                                          <Typography variant="body2">
                                            <strong>Age:</strong> {patientData.spouse.age} years
                                          </Typography>
                                        )}
                                        <Typography variant="body2">
                                          <strong>Occupation:</strong> {patientData.spouse.occupation}
                                        </Typography>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Relationship Status
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body2">
                                          <strong>Relationship:</strong> Spouse
                                        </Typography>
                                        {patientData.married_for && (
                                          <Typography variant="body2">
                                            <strong>Married For:</strong> {patientData.married_for}
                                          </Typography>
                                        )}
                                        {patientData.trying_for && (
                                          <Typography variant="body2">
                                            <strong>Trying For:</strong> {patientData.trying_for}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Paper>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                </MDBox>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <MDBox p={3}>
                  {/* Doctor Selection */}
                  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="textPrimary">
                      <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Doctor Selection
                    </Typography>
                    
                    {/* Doctor Dropdown Row */}
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                      <Controller
                        name="doctor_id"
                        control={control}
                        rules={{
                        required: "Please select a doctor"
                        }}
                        render={({ field }) => (
                        <FormControl fullWidth margin="normal" error={!!errors.doctor_id}>
                          <InputLabel id="doctor-select-label">
                          Consult With <span style={{ color: 'red' }}>*</span>
                          </InputLabel>
                          <Select
                          {...field}
                          labelId="doctor-select-label"
                          label={
                            <span>
                            Consult With <span style={{ color: 'red' }}>*</span>
                            </span>
                          }
                          sx={{ height: selectBoxHeight }}
                          value={field.value || ""}
                          >
                          <MenuItem value="" disabled>
                            <em>Select a doctor</em>
                          </MenuItem>
                          {doctors
                            .filter((doctor) => doctor && doctor.user_id && doctor.name)
                            .map((doctor, index) => (
                            <MenuItem 
                              key={`doctor-${doctor.user_id}-${index}`} 
                              value={doctor.user_id}
                            >
                            {doctor.name}
                            </MenuItem>
                          ))}
                          </Select>
                          {errors.doctor_id && (
                          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                            {errors.doctor_id.message}
                          </Typography>
                          )}
                        </FormControl>
                        )}
                      />
                      </Grid>
                    </Grid>
                  </Paper>

                              {/* Basic Vitals */}
                  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="textPrimary">
                      <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Basic Vitals
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="weight"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!value) return true; // Optional field
                              const weight = parseFloat(value);
                              if (isNaN(weight)) return "Weight must be a valid number";
                              if (weight <= 0) return "Weight must be greater than 0";
                              if (weight > 1000) return "Weight must be less than 1000 kg";
                              return true;
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Weight (kg)"
                              variant="outlined"
                              fullWidth
                              type="number"
                              inputProps={{ step: "0.01", min: "0", max: "1000" }}
                              error={!!errors.weight}
                              helperText={errors.weight?.message}
                              InputProps={{
                                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>kg</Typography>
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="height"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!value) return true; // Optional field
                              const height = parseFloat(value);
                              if (isNaN(height)) return "Height must be a valid number";
                              if (height <= 0) return "Height must be greater than 0";
                              if (height < 30) return "Height must be at least 30 cm";
                              if (height > 300) return "Height must be less than 300 cm";
                              return true;
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Height (cm)"
                              variant="outlined"
                              fullWidth
                              type="number"
                              inputProps={{ step: "0.1", min: "30", max: "300" }}
                              error={!!errors.height}
                              helperText={errors.height?.message}
                              InputProps={{
                                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>cm</Typography>
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Controller
                          name="bmi"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="BMI"
                              variant="outlined"
                              fullWidth
                              InputProps={{ 
                                readOnly: true,
                                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>kg/m²</Typography>
                              }}
                              sx={{ backgroundColor: "#f5f5f5" }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Controller
                          name="bp_systolic"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!value) return true; // Optional field
                              const systolic = parseInt(value);
                              if (isNaN(systolic)) return "Systolic BP must be a valid number";
                              if (systolic <= 0) return "Systolic BP must be greater than 0";
                              if (systolic < 50) return "Systolic BP must be at least 50 mmHg";
                              if (systolic > 300) return "Systolic BP must be less than 300 mmHg";
                              return true;
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Systolic BP"
                              variant="outlined"
                              fullWidth
                              type="number"
                              inputProps={{ min: "50", max: "300" }}
                              error={!!errors.bp_systolic}
                              helperText={errors.bp_systolic?.message}
                              InputProps={{
                                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>mmHg</Typography>
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Controller
                          name="bp_diastolic"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!value) return true; // Optional field
                              const diastolic = parseInt(value);
                              if (isNaN(diastolic)) return "Diastolic BP must be a valid number";
                              if (diastolic <= 0) return "Diastolic BP must be greater than 0";
                              if (diastolic < 30) return "Diastolic BP must be at least 30 mmHg";
                              if (diastolic > 200) return "Diastolic BP must be less than 200 mmHg";
                              
                              // Cross-validation: diastolic should be less than systolic
                              const systolic = parseInt(watch("bp_systolic"));
                              if (systolic && diastolic >= systolic) {
                                return "Diastolic BP must be less than Systolic BP";
                              }
                              return true;
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Diastolic BP"
                              variant="outlined"
                              fullWidth
                              type="number"
                              inputProps={{ min: "30", max: "200" }}
                              error={!!errors.bp_diastolic}
                              helperText={errors.bp_diastolic?.message}
                              InputProps={{
                                endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>mmHg</Typography>
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Remarks */}
                  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="textPrimary">
                      Additional Remarks
                    </Typography>
                    <Controller
                      name="remarks"
                      control={control}
                      rules={{
                        maxLength: {
                          value: 1000,
                          message: "Remarks cannot exceed 1000 characters"
                        }
                      }}
                      render={({ field }) => (
                        <RichTextEditor
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value)}
                          height="150px"
                          placeholder="Add any additional notes or instructions for this prescription..."
                        />
                      )}
                    />
                    {errors.remarks && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.remarks.message}
                      </Typography>
                    )}
                  </Paper>

                  {/* Submit Button */}
                  <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        padding: '10px 24px',
                        background: '#00798E',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        opacity: loading ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <SaveIcon sx={{ fontSize: 18 }} />
                      {loading ? "Creating..." : "Create Draft Prescription"}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/patient-list")}
                      disabled={loading}
                      style={{
                        padding: '10px 24px',
                        background: 'transparent',
                        color: '#00798E',
                        border: '1px solid #00798E',
                        borderRadius: 4,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        opacity: loading ? 0.6 : 1,
                      }}
                    >
                      Cancel
                    </button>
                  </MDBox>
                </MDBox>
              </form>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CreateDraftPrescription;
